import React, { useState } from 'react';

import { MailOutlined } from '@ant-design/icons';
import {
  Modal,
  Form,
  Input,
  Button,
  Space,
  Alert,
  message,
} from 'antd';
import { useTranslation } from 'react-i18next';

import { useAuth } from '@contexts/admin/AuthContext';
import { permissionTemplateService } from '@services/admin/permissionTemplateService';
import { filterGrantable } from '@utils/permissionSubset';
import {
  ACTIONS,
  RESOURCES_BY_ROLE,
  RESOURCE_ACTIONS_BY_ROLE,
} from '../../../permissions_manifest.generated';

import TemplateSelect from '../permissions/TemplateSelect';

/**
 * Admin 建立使用者邀請 Modal
 *
 * 對齊 CreateOperatorInvitationModal 的形狀，但 admin 無 warehouse/username 概念，
 * 故僅需 email 欄位 + 權限範本。
 *
 * @param {Boolean} open
 * @param {Function} onClose
 * @param {Function} onSuccess - 建立成功後的 callback（例如 refresh 列表、切換 tab）
 * @param {Object} userInvitationService - Admin user invitation service
 */
const CreateUserInvitationModal = ({
  open,
  onClose,
  onSuccess,
  userInvitationService,
}) => {
  const { t } = useTranslation();
  const { permissions: currentUserPermissions } = useAuth();
  const [form] = Form.useForm();
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [strippedCount, setStrippedCount] = useState(0);

  const handleClose = () => {
    onClose();
    form.resetFields();
    setSelectedPermissions([]);
    setStrippedCount(0);
  };

  const handleCreate = async (values) => {
    try {
      const { permission_template_id, ...rest } = values;
      // admin userInvitationService.create 接受 (merchantId, data)；admin 不帶 merchantId
      const result = await userInvitationService.create(null, {
        ...rest,
        permissions: selectedPermissions,
      });

      if (result.success) {
        message.success(t('admin.users.invite.create_success', {
          defaultValue: t('common.message.invite_success'),
        }));
        handleClose();
        onSuccess?.();

        if (result.data?.invitation_url) {
          Modal.info({
            title: t('admin.users.invite.link_title'),
            content: (
              <div>
                <p>{t('admin.users.invite.link_description')}</p>
                <Input.TextArea
                  value={result.data.invitation_url}
                  autoSize
                  readOnly
                  onClick={(e) => e.target.select()}
                  style={{ marginTop: 8 }}
                />
              </div>
            ),
            okText: t('common.button.close'),
          });
        }
      } else {
        const firstError = result.errors?.[0] || {};
        const { detail: errorCode, ...interpolations } = firstError;
        const fallback = result.message || t('common.error.invite_failed');
        const errorMessage = errorCode
          ? t(`admin.users.invite.error.${errorCode}`, { ...interpolations, defaultValue: fallback })
          : fallback;
        message.error(errorMessage);
      }
    } catch (error) {
      console.error('Failed to create invitation:', error);
      message.error(t('common.error.invite_failed'));
    }
  };

  return (
    <Modal
      title={
        <Space>
          <MailOutlined />
          {t('admin.users.invite.title')}
        </Space>
      }
      open={open}
      onCancel={handleClose}
      footer={null}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleCreate}
      >
        <Form.Item
          name="email"
          label={t('common.table.headers.email')}
          rules={[
            { required: true, message: t('common.validation.required') },
            { type: 'email', message: t('common.validation.email_invalid') }
          ]}
        >
          <Input
            placeholder="user@example.com"
            prefix={<MailOutlined />}
          />
        </Form.Item>

        <Form.Item
          name="permission_template_id"
          label={t('common.permissions_editor.select_template')}
        >
          <TemplateSelect
            templateService={permissionTemplateService}
            role="admin"
            onChange={(templateId, permissions) => {
              form.setFieldsValue({ permission_template_id: templateId });
              if (!currentUserPermissions || currentUserPermissions.length === 0) {
                setSelectedPermissions(permissions || []);
                setStrippedCount(0);
                return;
              }
              const { kept, dropped } = filterGrantable(
                permissions || [],
                currentUserPermissions,
                {
                  resources: RESOURCES_BY_ROLE.admin || [],
                  resourceActions: RESOURCE_ACTIONS_BY_ROLE.admin || {},
                  allActions: ACTIONS,
                }
              );
              setSelectedPermissions(kept);
              setStrippedCount(dropped.length);
            }}
          />
        </Form.Item>
        {strippedCount > 0 && (
          <Alert
            type="warning"
            showIcon
            style={{ marginTop: 8, marginBottom: 24 }}
            message={t('common.permissions_editor.template_stripped_count', { count: strippedCount })}
          />
        )}
        {selectedPermissions.length === 0 && (
          <Alert
            type="info"
            showIcon
            style={{ marginTop: 8, marginBottom: 24 }}
            message={t('admin.users.invite.permissions_required_hint')}
          />
        )}

        <Form.Item>
          <Space>
            <Button
              type="primary"
              htmlType="submit"
              disabled={selectedPermissions.length === 0}
            >
              {t('common.button.send')}
            </Button>
            <Button onClick={handleClose}>
              {t('common.button.cancel')}
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default CreateUserInvitationModal;
